import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Download, FileDown, FileText, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";

const ExportMenu = ({ storyId }) => {
  const { api } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = async (format) => {
    setShowMenu(false);
    setExporting(true);
    try {
      const endpoints = {
        pdf: `/stories/${storyId}/export-pdf`,
        docx: `/stories/${storyId}/export-docx`,
        txt: `/stories/${storyId}/export-txt`
      };
      const mimeTypes = {
        pdf: 'application/pdf',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        txt: 'text/plain'
      };
      const res = await api.get(endpoints[format], { responseType: 'blob' });
      const blob = new Blob([res.data], { type: mimeTypes[format] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `story.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      
      alert(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setShowMenu(!showMenu)}
        disabled={exporting}
        className="text-text-muted"
        data-testid="export-button"
        title="Download / Export"
      >
        {exporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
      </Button>
      {showMenu && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-[#E5E2DC] z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="px-4 py-2.5 border-b border-[#E5E2DC]/50">
            <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Export As</span>
          </div>
          <button
            onClick={() => handleExport('pdf')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:bg-surface-alt hover:text-text-primary transition-colors"
            data-testid="export-pdf-option"
          >
            <FileDown className="w-4 h-4 text-red-500" /> PDF Document
          </button>
          <button
            onClick={() => handleExport('docx')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:bg-surface-alt hover:text-text-primary transition-colors"
            data-testid="export-docx-option"
          >
            <FileText className="w-4 h-4 text-blue-500" /> Word Document (.docx)
          </button>
          <button
            onClick={() => handleExport('txt')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:bg-surface-alt hover:text-text-primary transition-colors"
            data-testid="export-txt-option"
          >
            <FileText className="w-4 h-4 text-gray-500" /> Plain Text (.txt)
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportMenu;
